import { Modal, Select, Button, Space, message, Spin } from "antd"
import { collection, getDocs, query, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore"
import React, { useEffect, useState } from "react"
import { db } from "../../../firebase/config"
import { useAuthContext } from "../../../hooks/useAuthContext"

export default function DuplicateTransferModal({ open, onClose, selectedPrices, priceData }) {
  const { user } = useAuthContext()
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [loading, setLoading] = useState(false)
  const [clientsLoading, setClientsLoading] = useState(false)

  // Fetch all clients
  useEffect(() => {
    if (!open) return

    const fetchClients = async () => {
      setClientsLoading(true)
      try {
        const q = query(collection(db, "clients"))
        const snapshot = await getDocs(q)
        const clientsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setClients(clientsList)
      } catch (error) {
        console.error("Error fetching clients:", error)
        message.error("Failed to load clients")
      } finally {
        setClientsLoading(false)
      }
    }

    fetchClients()
  }, [open])

  const handleDuplicateAndTransfer = async () => {
    if (!selectedClient) {
      message.error("Please select a client")
      return
    }

    if (!selectedPrices || selectedPrices.length === 0) {
      message.error("Please select prices to duplicate")
      return
    }

    setLoading(true)
    try {
      const selectedClientData = clients.find((c) => c.id === selectedClient)
      const now = new Date()
      const timestamp = {
        seconds: Math.floor(now.getTime() / 1000),
        nanoseconds: (now.getTime() % 1000) * 1000000,
      }

      // Get full price data for selected prices
      const pricesToDuplicate = priceData.filter((price) =>
        selectedPrices.includes(price.id)
      )

      // Duplicate each price
      for (const price of pricesToDuplicate) {
        const duplicated = {
          ...price,
          // Update all client info from new client document
          ...selectedClientData,
          // Update user info
          user: user.uid,
          userEmail: user.email,
          // Update timestamps with proper structure
          timestamp: timestamp,
          updated: timestamp,
          updatedBy: user.uid,
          date: {
            created: timestamp,
            lastEdit: timestamp,
          },
        }

        // Remove the id field so Firestore generates a new one
        delete duplicated.id

        await addDoc(collection(db, "priceCalculation"), duplicated)
      }

      message.success(
        `Successfully duplicated ${pricesToDuplicate.length} price(s) for ${selectedClientData.client}`
      )
      onClose()
      setSelectedClient(null)
    } catch (error) {
      console.error("Error duplicating prices:", error)
      message.error("Failed to duplicate prices")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Duplicate and Transfer to Client"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleDuplicateAndTransfer}
          disabled={!selectedClient || !selectedPrices || selectedPrices.length === 0}>
          Duplicate ({selectedPrices?.length || 0})
        </Button>,
      ]}>
      <div style={{ marginBottom: 16 }}>
        <p>
          <strong>Selected prices:</strong> {selectedPrices?.length || 0}
        </p>
      </div>

      <Spin spinning={clientsLoading}>
        <Select
          placeholder="Search and select a client to transfer to"
          style={{ width: "100%" }}
          value={selectedClient}
          onChange={setSelectedClient}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={clients.map((client) => ({
            value: client.id,
            label: client.client,
          }))}
        />
      </Spin>
    </Modal>
  )
}
